import { Trans, useLingui } from '@lingui/react/macro';
import { animated, easings, useInView, useSpring } from '@react-spring/web';
import { MessageQuestion } from 'iconsax-reactjs';
import type { FC } from 'react';
import { useHasAccessToken } from '@/auth/api';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import { Button } from '@/shared/Button';
import { Logo } from '@/shared/Logo';
import { cn } from '@/shared/react';
import { useExternalLinks } from '../../useExternalLinks';
import backdrop from './assets/backdrop.png?w=500&quality=50&format=png&effort=max&img';
import chip from './assets/chip.webp?w=238&quality=75&format=png&effort=max&img';
import instagramIcon from './assets/instagram.png';
import linkedinIcon from './assets/linkedin.png';
import telegramIcon from './assets/telegram.png';
import twitterIcon from './assets/twitter.png';

export const Footer: FC<{ className?: string }> = ({ className }) => {
  const { t } = useLingui();
  const links = useExternalLinks();
  const [{ language }] = useSettings();

  const hasAccessToken = useHasAccessToken();
  const [ref, inView] = useInView();

  const img = useSpring({
    y: inView ? 0 : -300,
    delay: 150,
    config: { easing: easings.easeInBounce },
  });

  return (
    <footer
      className={cn(
        'bg-neutral-950 text-shades-white-100 h-auto relative text-center overflow-hidden',
        className,
      )}
      ref={ref}
    >
      <img
        src={backdrop}
        alt="Backdrop"
        width="100%"
        className="absolute w-full h-1/2 top-0 object-contain"
        loading="lazy"
      />
      <div className="h-36 overflow-visible flex items-end justify-center">
        <animated.img
          style={img}
          src={chip}
          alt="Chips"
          className="h-[186px] absolute top-0 w-auto max-w-full object-contain object-bottom"
          width={238}
          height={186}
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="flex flex-col items-center relative mb-10 px-3">
        <p className="text-lg mb-2">{t`Secure. Local. Built for You`}</p>
        <h2 className="text-2xl desktop:text-4xl leading-tight mb-5">
          <Trans>
            Iraq’s First
            <br />
            Crypto Exchange
          </Trans>
        </h2>
        <Link
          workspace="app"
          to={!hasAccessToken ? '/auth' : '/dashboard'}
          className="block max-w-2xs w-full mb-12"
        >
          <Button size={12} variant="brand" block className="w-full">
            {t`Get Started`}
          </Button>
        </Link>

        <Link to="/" workspace="public" className="block max-w-28 w-full mb-3">
          <Logo width="112px" height="55px" text dark />
        </Link>
        <p className="text-sm text-shades-white-60 mb-widget">
          {t`Trade with confidence on the only exchange fully built for your region.`}
        </p>
        <div className="flex gap-3 items-center mb-8">
          <a
            href={links.telegram}
            className="contents"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >
            <Button
              type="div"
              variant="transparent"
              className="bg-transparent"
              size={8}
              fab
            >
              <img src={telegramIcon} alt="Telegram" width={20} height={20} />
            </Button>
          </a>
          <a
            href={links.linkedin}
            className="contents"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >
            <Button
              type="div"
              variant="transparent"
              className="bg-transparent"
              size={8}
              fab
            >
              <img src={linkedinIcon} alt="Linkedin" width={20} height={20} />
            </Button>
          </a>
          <a
            href={links.instagram}
            className="contents"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >
            <Button
              type="div"
              variant="transparent"
              className="bg-transparent"
              size={8}
              fab
            >
              <img src={instagramIcon} alt="Instagram" width={20} height={20} />
            </Button>
          </a>
          <a
            href={links.twitter}
            className="contents"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >
            <Button
              type="div"
              variant="transparent"
              className="bg-transparent"
              size={8}
              fab
            >
              <img src={twitterIcon} alt="Twitter" width={20} height={20} />
            </Button>
          </a>
        </div>
      </div>
      <div className="bg-neutral-900 py-3 flex desktop:flex-row flex-col gap-3 items-center justify-between px-3 text-neutral-200">
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-2 desktop:flex-nowrap desktop:justify-start desktop:gap-8 items-center">
          <a
            href={links.faq}
            className="hover:underline"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >{t`FAQ`}</a>
          <Link
            workspace="public"
            to={`/${language}/fee`}
            className="hover:underline"
          >{t`Fees`}</Link>
          <a
            href={links.termsOfUse}
            className="hover:underline"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >{t`Terms of Use`}</a>
          <a
            href={links.privacyPolicy}
            className="hover:underline"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >{t`Privacy Policy`}</a>
          <a
            href={links.blog}
            className="hover:underline"
            target="_blank"
            referrerPolicy="no-referrer"
            rel="noopener"
          >
            {t`Blog`}
          </a>
          <a
            href={`mailto:${links.supportEmail}`}
            target="_blank"
            referrerPolicy="no-referrer"
            className="flex items-center justify-center gap-1 hover:underline"
            rel="noopener"
          >
            <MessageQuestion size={20} className="-mb-1" /> {links.supportEmail}
          </a>
        </div>
        <p className="text-base">{t`© 2026 IQX, Inc. | All Rights Reserved`}</p>
      </div>
    </footer>
  );
};
